import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
// ...existing code...
import './ManagementPages.css';

const AgentCollectionSummary = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAgentCollectionSummary();
  }, []);

  const fetchAgentCollectionSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reportsAPI.getAgentCollectionSummary();
      const list = (data && (data.data || data)) || [];
      setAgents(list);
    } catch (err) {
      setError(err.message || 'Failed to load agent collection summary');
    } finally {
      setLoading(false);
    }
  };

  const getTotalStats = () => {
    if (!agents || agents.length === 0) return null;
    
    return {
      totalAgents: agents.length,
      totalRetailers: agents.reduce((sum, a) => sum + (a.total_retailers || 0), 0),
      totalLoans: agents.reduce((sum, a) => sum + (a.total_loans || 0), 0),
      totalLoanAmount: agents.reduce((sum, a) => sum + (a.total_loan_amount || 0), 0),
      todaysTarget: agents.reduce((sum, a) => sum + (a.todays_collection_target || 0), 0),
      todaysActual: agents.reduce((sum, a) => sum + (a.todays_collection_actual || 0), 0),
      totalCollected: agents.reduce((sum, a) => sum + (a.total_collected || 0), 0),
      pendingRecovery: agents.reduce((sum, a) => sum + (a.pending_recovery || 0), 0),
    };
  };

  const stats = getTotalStats();

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Agent Collection Summary</h1>
        <p>Track agent-wise collections, targets, and recovery status</p>
      </div>

      {/* Overall Stats */}
      {stats && (
        <div
          className="grid grid-4 agent-stats-grid"
          style={{
            marginBottom: '30px',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '18px',
          }}
        >
          <div className="dashboard-card">
            <div className="card-icon">👥</div>
            <div className="card-content">
              <div className="card-label">Total Agents</div>
              <div className="card-value">{stats.totalAgents}</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">🏪</div>
            <div className="card-content">
              <div className="card-label">Total Retailers</div>
              <div className="card-value">{stats.totalRetailers}</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">📋</div>
            <div className="card-content">
              <div className="card-label">Total Loans</div>
              <div className="card-value">{stats.totalLoans}</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <div className="card-label">Total Loan Amount</div>
              <div className="card-value">₹{(stats.totalLoanAmount / 100000).toFixed(2)}L</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">🎯</div>
            <div className="card-content">
              <div className="card-label">Today's Collection Target</div>
              <div className="card-value">₹{(stats.todaysTarget / 1000).toFixed(2)}K</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">✅</div>
            <div className="card-content">
              <div className="card-label">Today's Collection (Actual)</div>
              <div className="card-value">₹{(stats.todaysActual / 1000).toFixed(2)}K</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <div className="card-label">Total Collected (All Time)</div>
              <div className="card-value">₹{(stats.totalCollected / 100000).toFixed(2)}L</div>
            </div>
          </div>
          <div className="dashboard-card">
            <div className="card-icon">⚠️</div>
            <div className="card-content">
              <div className="card-label">Pending Recovery</div>
              <div className="card-value">₹{(stats.pendingRecovery / 100000).toFixed(2)}L</div>
            </div>
          </div>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loader"></div>
      ) : agents.length > 0 ? (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Agent Name</th>
                <th>Phone</th>
                <th>Retailers</th>
                <th>Total Loans</th>
                <th>Active Loans</th>
                <th>Total Loan Amount</th>
                <th>Today's Target</th>
                <th>Today's Collection</th>
                <th>Total Collected</th>
                <th>Pending Recovery</th>
                <th>Recovery %</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => {
                const loanAmount = agent.total_loan_amount || 0;
                const collected = agent.total_collected || 0;
                const recoveryPercent = loanAmount > 0 ? ((collected / loanAmount) * 100).toFixed(2) : 0;
                const todaysPercent = (agent.todays_collection_target || 0) > 0 
                  ? ((agent.todays_collection_actual || 0) / (agent.todays_collection_target || 1) * 100).toFixed(2)
                  : 0;
                
                return (
                  <tr key={agent.id}>
                    <td><strong>{agent.first_name} {agent.last_name}</strong></td>
                    <td>{agent.phone}</td>
                    <td>{agent.total_retailers || 0}</td>
                    <td>{agent.total_loans || 0}</td>
                    <td><span className="badge badge-active">{agent.active_loans || 0}</span></td>
                    <td>₹{(loanAmount / 100000).toFixed(2)}L</td>
                    <td>₹{(agent.todays_collection_target || 0).toLocaleString()}</td>
                    <td>
                      <strong>₹{(agent.todays_collection_actual || 0).toLocaleString()}</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>({todaysPercent}%)</div>
                    </td>
                    <td>₹{(collected / 100000).toFixed(2)}L</td>
                    <td>
                      <span style={{ color: '#e74c3c' }}>
                        ₹{((agent.pending_recovery || 0) / 100000).toFixed(2)}L
                      </span>
                    </td>
                    <td>
                      <div style={{
                        background: '#ecf0f1',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: recoveryPercent > 80 ? '#27ae60' : recoveryPercent > 50 ? '#f39c12' : '#e74c3c'
                      }}>
                        {recoveryPercent}%
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">No agents found</div>
      )}

      <button 
        className="button button-primary" 
        onClick={fetchAgentCollectionSummary}
        style={{ marginTop: '20px' }}
      >
        Refresh
      </button>
    </div>
  );
};

export default AgentCollectionSummary;
